import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faChartLine, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../../../contexts/AuthContext";
import LoadingAction from "../../../themes/LoadingAction/LoadingAction";
import "./CentralTelemetria.css";

const dinheiro = (valor) => Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const iso = (ano, mes, dia) => `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;

const normalizarClientes = (dados) => {
  if (Array.isArray(dados)) return dados;
  if (Array.isArray(dados?.clientes)) return dados.clientes;
  if (Array.isArray(dados?.data)) return dados.data;
  return [];
};

const periodoConsulta = (tipo, mesRef, anoRef) => {
  if (tipo === "TODO") return { inicio: "", fim: "", descricao: "Todo o período disponível" };
  if (tipo === "MENSAL") {
    const [ano, mes] = String(mesRef || "").split("-").map(Number);
    if (!ano || !mes) return null;
    const ultimoDia = new Date(ano, mes, 0).getDate();
    return { inicio: iso(ano, mes, 1), fim: iso(ano, mes, ultimoDia), descricao: `Mês completo: 01/${String(mes).padStart(2, "0")}/${ano} a ${String(ultimoDia).padStart(2, "0")}/${String(mes).padStart(2, "0")}/${ano}` };
  }
  const ano = Number(anoRef);
  if (!ano) return null;
  return { inicio: iso(ano, 1, 1), fim: iso(ano, 12, 31), descricao: `Ano completo: 01/01/${ano} a 31/12/${ano}` };
};

const CentralTelemetria = () => {
  const { authInfo } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const headers = useMemo(() => ({ "x-access-token": token, "content-type": "application/json" }), [token]);
  const hoje = new Date();
  const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState({});
  const [clientes, setClientes] = useState([]);
  const [financeiroAberto, setFinanceiroAberto] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const [periodo, setPeriodo] = useState("ANUAL");
  const [mesRef, setMesRef] = useState(mesAtual);
  const [anoRef, setAnoRef] = useState(String(hoje.getFullYear()));
  const [relatorio, setRelatorio] = useState(null);
  const [consultaDescricao, setConsultaDescricao] = useState("");
  const [financeiroLoading, setFinanceiroLoading] = useState(false);
  const [financeiroErro, setFinanceiroErro] = useState("");

  const carregarClientes = async () => {
    const resultados = await Promise.allSettled([
      axios.get(`${process.env.REACT_APP_SERVIDOR}/admin-2027/clientes`, { headers }),
      axios.get(`${process.env.REACT_APP_SERVIDOR}/clientes`, { headers }),
    ]);

    const porId = new Map();
    resultados.forEach((resultado) => {
      if (resultado.status !== "fulfilled") return;
      normalizarClientes(resultado.value?.data).forEach((cliente) => {
        if (!cliente?.id || cliente?.arquivado === true) return;
        porId.set(cliente.id, { ...(porId.get(cliente.id) || {}), ...cliente });
      });
    });

    return Array.from(porId.values()).sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
    );
  };

  const carregarFallback = async () => {
    const [clientesRes, maquinasRes] = await Promise.all([
      axios.get(`${process.env.REACT_APP_SERVIDOR}/clientes`, { headers }),
      axios.get(`${process.env.REACT_APP_SERVIDOR}/machines-adm`, { headers }),
    ]);
    const lista = normalizarClientes(clientesRes.data)
      .filter((cliente) => cliente?.id && cliente?.arquivado !== true)
      .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
    const dadosMaq = maquinasRes.data || {};
    setClientes(lista);
    setResumo({ clientes: lista.length, maquinas: dadosMaq.total || 0, online: dadosMaq.online || 0, offline: dadosMaq.offline || 0, mensalidadesVencidas: 0 });
  };

  const carregar = async () => {
    setLoading(true);
    try {
      const [resumoResultado, clientesResultado, maquinasResultado] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_SERVIDOR}/admin-2027/resumo`, { headers }),
        carregarClientes(),
        axios.get(`${process.env.REACT_APP_SERVIDOR}/machines-adm`, { headers }),
      ]);

      const listaClientes = clientesResultado.status === "fulfilled" ? clientesResultado.value : [];
      const resumoServidor = resumoResultado.status === "fulfilled" ? (resumoResultado.value?.data || {}) : {};
      const maquinasServidor = maquinasResultado.status === "fulfilled" ? (maquinasResultado.value?.data || {}) : {};

      if (!listaClientes.length && resumoResultado.status === "rejected" && maquinasResultado.status === "rejected") {
        await carregarFallback();
        return;
      }

      const resumoClientes = Number(resumoServidor.clientes || 0);
      const resumoMaquinas = Number(resumoServidor.maquinas || 0);

      setClientes(listaClientes);
      setResumo({
        ...resumoServidor,
        clientes: resumoClientes > 0 ? resumoClientes : listaClientes.length,
        maquinas: resumoMaquinas > 0 ? resumoMaquinas : Number(maquinasServidor.total || 0),
        online: resumoMaquinas > 0 ? Number(resumoServidor.online || 0) : Number(maquinasServidor.online || 0),
        offline: resumoMaquinas > 0 ? Number(resumoServidor.offline || 0) : Number(maquinasServidor.offline || 0),
        mensalidadesVencidas: Number(resumoServidor.mensalidadesVencidas || 0),
      });
    } catch (e) {
      try { await carregarFallback(); } catch (_) { setResumo({}); setClientes([]); }
    } finally { setLoading(false); }
  };

  const consultarFinanceiro = async () => {
    if (!clienteId) { setFinanceiroErro("Selecione primeiro a empresa que deseja consultar."); setRelatorio(null); return; }
    const faixa = periodoConsulta(periodo, mesRef, anoRef);
    if (!faixa) { setFinanceiroErro("Informe um período válido."); setRelatorio(null); return; }
    setFinanceiroLoading(true);
    setFinanceiroErro("");
    try {
      const params = new URLSearchParams({ clienteId });
      if (faixa.inicio) params.set("inicio", faixa.inicio);
      if (faixa.fim) params.set("fim", faixa.fim);
      const res = await axios.get(`${process.env.REACT_APP_SERVIDOR}/admin-2027/relatorio-empresas?${params.toString()}`, { headers });
      const lista = Array.isArray(res.data) ? res.data : [];
      setRelatorio(lista[0] || { clienteId, cliente: clientes.find((c) => c.id === clienteId)?.nome || "Empresa", quantidade: 0, bruto: 0, taxas: 0, estornos: 0, liquido: 0 });
      setConsultaDescricao(faixa.descricao);
    } catch (e) {
      setRelatorio(null);
      setFinanceiroErro(e.response?.data?.error || "Não foi possível consultar o financeiro agora.");
    } finally { setFinanceiroLoading(false); }
  };

  useEffect(() => {
    carregar();
    const timer = setInterval(carregar, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="central-telemetria">
      {loading && <LoadingAction />}
      <div className="central-topo">
        <div>
          <div className="central-kicker">VISÃO GERAL</div>
          <h2>Central de Telemetria</h2>
        </div>
        <Button className="central-atualizar" onClick={carregar}>
          <FontAwesomeIcon icon={faArrowsRotate} style={{ marginRight: 6 }} /> Atualizar
        </Button>
      </div>
      <div className="central-cards">
        <div><span>Empresas</span><strong>{resumo.clientes ?? 0}</strong><small>cadastradas</small></div>
        <div><span>Máquinas</span><strong>{resumo.maquinas ?? 0}</strong><small>total da frota</small></div>
        <div className="ok"><span>Online</span><strong>{resumo.online ?? 0}</strong><small>com comunicação</small></div>
        <div className="erro"><span>Offline</span><strong>{resumo.offline ?? 0}</strong><small>sem comunicação</small></div>
        <div className="atencao"><span>Mensalidades vencidas</span><strong>{resumo.mensalidadesVencidas ?? 0}</strong><small>precisam de atenção</small></div>
      </div>
      <section className="central-financeiro-discreto">
        <button className="central-financeiro-toggle" onClick={() => setFinanceiroAberto((v) => !v)}>
          <span><FontAwesomeIcon icon={faChartLine} /> Consulta financeira por empresa</span>
          <FontAwesomeIcon icon={financeiroAberto ? faChevronUp : faChevronDown} />
        </button>
        {financeiroAberto && (
          <div className="central-financeiro-corpo">
            <div className="central-financeiro-explicacao">O financeiro só aparece quando você escolher uma empresa e o período desejado.</div>
            <div className="central-financeiro-filtros">
              <label><span>Empresa</span><select value={clienteId} onChange={(e) => { setClienteId(e.target.value); setRelatorio(null); }}><option value="">Selecione uma empresa</option>{clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</select></label>
              <label><span>Período</span><select value={periodo} onChange={(e) => { setPeriodo(e.target.value); setRelatorio(null); }}><option value="ANUAL">Anual</option><option value="MENSAL">Mensal</option><option value="TODO">Todo o período</option></select></label>
              {periodo === "MENSAL" && <label><span>Mês</span><input type="month" value={mesRef} onChange={(e) => { setMesRef(e.target.value); setRelatorio(null); }} /></label>}
              {periodo === "ANUAL" && <label><span>Ano</span><input type="number" min="2020" max="2100" value={anoRef} onChange={(e) => { setAnoRef(e.target.value); setRelatorio(null); }} /></label>}
              <Button className="central-consultar" onClick={consultarFinanceiro} loading={financeiroLoading}>Consultar</Button>
            </div>
            {financeiroErro && <div className="central-financeiro-erro">{financeiroErro}</div>}
            {relatorio && (
              <div className="central-resultado-financeiro">
                <div className="central-resultado-cabecalho"><div><span>EMPRESA CONSULTADA</span><h3>{relatorio.cliente}</h3><small>{consultaDescricao}</small></div></div>
                <div className="central-resultado-grid">
                  <div><span>Pagamentos</span><strong>{relatorio.quantidade ?? 0}</strong></div>
                  <div><span>Bruto</span><strong>{dinheiro(relatorio.bruto)}</strong></div>
                  <div><span>Taxas</span><strong>{dinheiro(relatorio.taxas)}</strong></div>
                  <div><span>Estornos</span><strong>{dinheiro(relatorio.estornos)}</strong></div>
                  <div className="liquido"><span>Líquido</span><strong>{dinheiro(relatorio.liquido)}</strong></div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default CentralTelemetria;
